"use client";

import { useState } from 'react';
import { InquiryForm } from '@/components/InquiryForm';
import { cn } from "@/lib/utils";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useMutation } from "convex/react";
import { api } from "../../convex/_generated/api";
import { useToast } from "@/hooks/use-toast";
import { Mail, Send, Sparkles, MessageSquare } from "lucide-react";

export function ContactCard({ className, children }: { className?: string, children?: React.ReactNode }) {
    const { toast } = useToast();
    const subscribe = useMutation(api.mailingList.subscribe);

    // Newsletter State
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubscribe = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email) return;

        setIsSubmitting(true);
        try {
            await subscribe({ email, name, source: "website_footer_card" });
            toast({
                title: "Welcome to the family! 🐾",
                description: "You've been added to our VIP waiting list. We'll verify your email shortly.",
            });
            setName("");
            setEmail("");
        } catch (error) {
            console.error(error);
            toast({
                title: "Something went wrong",
                description: "Please try again later.",
                variant: "destructive",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className={cn("bg-card/90 backdrop-blur-xl p-8 md:p-12 rounded-[3rem] shadow-soft border border-border/50 relative overflow-hidden", className)}>
            {/* Decorative background elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl -z-10 translate-x-1/2 -translate-y-1/2" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-secondary/20 rounded-full blur-3xl -z-10 -translate-x-1/2 translate-y-1/2" />

            <Tabs defaultValue="message" className="w-full">
                <div className="flex flex-col items-center mb-10">
                    <TabsList className="grid w-full max-w-md grid-cols-2 p-1 bg-muted/50 rounded-full">
                        <TabsTrigger value="message" className="rounded-full data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all duration-300">
                            <MessageSquare className="w-4 h-4 mr-2" /> Message Us
                        </TabsTrigger>
                        <TabsTrigger value="newsletter" className="rounded-full data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all duration-300">
                            <Mail className="w-4 h-4 mr-2" /> VIP Updates
                        </TabsTrigger>
                    </TabsList>
                </div>

                <TabsContent value="message" className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                    <div className="text-center mb-8">
                        <span className="text-[10px] font-black text-foreground/60 uppercase tracking-[0.3em] mb-4 block">Get in Touch</span>
                        <h2 className="font-headline text-4xl md:text-5xl font-black text-foreground">Let's <span className="text-primary italic font-normal">Connect</span></h2>
                        <p className="text-muted-foreground mt-4 font-medium max-w-lg mx-auto">
                            Have a specific question about a puppy? Send us a message and we'll get back to you within 24 hours.
                        </p>
                    </div>
                    <InquiryForm onFormSubmit={() => { }} isEmbedded={true} hideHeader={true} />
                </TabsContent>

                <TabsContent value="newsletter" className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                    <div className="text-center mb-10">
                        <div className="w-16 h-16 bg-primary/10 text-primary rounded-2xl flex items-center justify-center mx-auto mb-6">
                            <Sparkles className="w-8 h-8" />
                        </div>
                        <h2 className="font-headline text-4xl md:text-5xl font-black text-foreground mb-4">
                            VIP <span className="text-primary font-normal">Access</span>
                        </h2>
                        <p className="text-lg text-muted-foreground max-w-xl mx-auto leading-relaxed">
                            Join our exclusive community to receive notifications about new litters before they are posted to the public.
                        </p>
                    </div>

                    <form onSubmit={handleSubscribe} className="max-w-md mx-auto space-y-4">
                        <div className="space-y-2">
                            <Input
                                placeholder="Your Name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className="h-14 rounded-2xl bg-background/50 border-input focus:ring-primary/20 transition-all text-lg"
                            />
                        </div>
                        <div className="space-y-2">
                            <Input
                                type="email"
                                placeholder="Email Address"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="h-14 rounded-2xl bg-background/50 border-input focus:ring-primary/20 transition-all text-lg"
                            />
                        </div>
                        <Button
                            type="submit"
                            size="lg"
                            className="w-full h-14 rounded-2xl text-lg font-bold shadow-soft hover:shadow-soft-hover transition-all hover:scale-[1.02]"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? "Joining..." : "Get Early Access"}
                            {!isSubmitting && <Send className="w-5 h-5 ml-2" />}
                        </Button>
                        <p className="text-xs text-center text-muted-foreground mt-4">
                            We respect your inbox. No spam, just puppies.
                        </p>
                    </form>
                </TabsContent>
            </Tabs>

            {children}
        </div>
    );
}
